package com.eladfinish.bible.contest.screens.search.usecases

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.StringConstants.ALL_CHAPTERS
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.screens.search.engine.ChapterSearchInput
import com.eladfinish.bible.contest.screens.search.model.LocalSearchRequest
import com.eladfinish.bible.contest.screens.search.model.LocalSearchResult
import com.eladfinish.bible.contest.screens.search.model.SearchDebugContext
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.usecases.engine.SearchChapterUseCase
import com.eladfinish.bible.contest.search_has_been_interrupted
import com.eladfinish.bible.contest.search_has_ended
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toImmutableList

class ExecuteLocalSearchUseCase(
    private val assetReader: AssetReader,
    private val searchUseCase: SearchUseCase,
    private val searchChapterUseCase: SearchChapterUseCase,
    private val searchChapterMatchesToSearchResultUiModelsUseCase: SearchChapterMatchesToSearchResultUiModelsUseCase,
) {

    suspend operator fun invoke(
        request: LocalSearchRequest,
        shouldKeepRunning: () -> Boolean,
        onProgress: (book: String, chapter: String, resultsCount: Int) -> Unit,
        onResultsFound: (ImmutableList<VerseUiModel>) -> Unit,
        buildTerminationMessage: (StringResource) -> StringDesc,
        buildErrorMessage: (Throwable, SearchDebugContext) -> String,
    ): LocalSearchResult {
        val debug = SearchDebugContext()
        val allResults = mutableListOf<VerseUiModel>()

        return try {
            val prepared = searchUseCase(
                originalExpression = request.originalExpression,
                expression = request.regexInput,
                withNikudAndTeamim = request.withNikudAndTeamim,
                searchMode = request.searchMode,
            )

            debug.regex = prepared.regexResult

            if (prepared.actualRegex == null) {
                return LocalSearchResult.Completed(
                    results = persistentListOf(),
                    differentVersesNum = 0,
                    message = prepared.string.desc(),
                    finalRegex = prepared.regexResult,
                    wasInterrupted = false,
                )
            }

            // On web, AssetReader.readFile() per chapter is a blocking synchronous XHR, and
            // Kotlin/JS coroutines all run on the single browser thread regardless of dispatcher
            // (unlike JVM/Native's real IO thread pool) — so scanning the whole Bible one
            // chapter at a time froze the tab for the entire search, with the progress dialog
            // unable to render a single frame. Warming the cache first via concurrent async
            // fetches (see AssetReader.prefetch) turns those into instant cache hits below.
            assetReader.prefetch(
                request.searchedChapters.map { ALL_CHAPTERS + assetReader.fileSeparator + it.filename },
            )

            for (chapter in request.searchedChapters) {
                if (!shouldKeepRunning()) {
                    return LocalSearchResult.Completed(
                        results = allResults.toImmutableList(),
                        differentVersesNum = allResults.countDifferentVerses(),
                        message = buildTerminationMessage(
                            SharedRes.strings.search_has_been_interrupted,
                        ),
                        finalRegex = prepared.regexResult,
                        wasInterrupted = true,
                    )
                }

                val chapterContent = assetReader.readFile(
                    fileName = ALL_CHAPTERS + assetReader.fileSeparator + chapter.filename,
                )

                val chapterResult = searchChapterUseCase(
                    input = ChapterSearchInput(
                        bookIndex = chapter.bookIndex.minus(1), // due to: ChaptersRepository.getBookNumber(chapterFilename = it)
                        rawChapterText = chapterContent,
                        preparedRegex = prepared.actualRegex,
                        firstColonIsPartOfRegex = prepared.firstColonIsPartOfRegex,
                        searchMode = request.searchMode,
                        withNikud = request.withNikud,
                        withTeamim = request.withTeamim,
                        onlyQri = request.onlyQri,
                        isPremium = request.isPremium,
                    ),
                )

                debug.bookName = chapterResult.bookName
                debug.chapterName = chapterResult.chapterName
                debug.content = chapterContent

                onProgress(
                    chapterResult.bookName,
                    chapterResult.chapterName,
                    allResults.size,
                )

                val mappedResults = searchChapterMatchesToSearchResultUiModelsUseCase(
                    matches = chapterResult.matches,
                    isPremium = request.isPremium,
                    startIndex = allResults.size,
                )

                if (mappedResults.isNotEmpty()) {
                    onResultsFound(mappedResults)
                }

                allResults += mappedResults
            }

            LocalSearchResult.Completed(
                results = allResults.toImmutableList(),
                differentVersesNum = allResults.countDifferentVerses(),
                message = buildTerminationMessage(
                    SharedRes.strings.search_has_ended,
                ),
                finalRegex = prepared.regexResult,
                wasInterrupted = false,
            )
        } catch (t: Throwable) {
            LocalSearchResult.Failure(
                message = buildErrorMessage(t, debug),
                throwable = t,
                resultsCount = allResults.size,
            )
        }
    }

    private fun List<VerseUiModel>.countDifferentVerses(): Int =
        map { Triple(it.bookIndex, it.chapterIndex, it.verse) }
            .distinct()
            .size
}
